import React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiExternalLink, FiInfo, FiStar } from 'react-icons/fi';
import { Seo } from '../components/Seo';
import { site } from '../config/site';
import { getOffer } from '../data/offers';
import { MiniBarChart } from '../components/charts/MiniBarChart';
import { ScoreRing } from '../components/charts/ScoreRing';
import { TimelineChart } from '../components/charts/TimelineChart';
import { Icon } from '../components/ui/Icon';

function Stars({ value }: { value: number }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  const total = 5;

  return (
    <div className="flex items-center gap-1" aria-label={`Note: ${value} sur 5`}>
      {Array.from({ length: total }).map((_, i) => {
        const filled = i < full || (i === full && half);
        return (
          <Icon
            icon={FiStar}
            key={i}
            className={filled ? 'text-fuchsia-300' : 'text-white/20'}
            aria-hidden="true"
          />
        );
      })}
      <span className="ml-2 text-sm font-bold text-white/90">{value.toFixed(1)}/5</span>
    </div>
  );
}

export default function OfferReviewPage() {
  const { slug } = useParams();
  const offer = slug ? getOffer(slug) : undefined;

  if (!offer) {
    return (
      <section className="lbf-container py-16">
        <div className="lbf-card p-6">
          <div className="text-2xl font-black text-white">Offre introuvable</div>
          <p className="mt-3 text-sm text-zinc-200/80">
            Le lien n’est pas valide. Retourne à la liste des offres pour choisir un avis.
          </p>
          <NavLink to="/offres" className="lbf-btn-primary mt-6">
            Voir les offres <Icon icon={FiArrowRight} aria-hidden="true" />
          </NavLink>
        </div>
      </section>
    );
  }

  const canonicalPath = `/offres/${offer.slug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    url: `${site.url}${canonicalPath}`,
    inLanguage: 'fr-FR',
    datePublished: offer.updatedAtISO,
    author: {
      '@type': 'Organization',
      name: site.name,
      url: site.url,
    },
    itemReviewed: {
      '@type': 'Service',
      name: `${offer.brand} — Paris sportifs`,
      areaServed: 'FR',
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: offer.rating,
      bestRating: 5,
      worstRating: 0,
    },
    headline: `${offer.brand}: avis, bonus et retraits`,
    reviewBody: offer.heroPitch,
  };

  return (
    <>
      <Seo
        title={`${offer.brand}: avis & bonus`}
        description={`${offer.brand} en détail: bonus de bienvenue, conditions, retraits, cotes et profil de joueur. Analyse claire + graphiques.`}
        canonicalPath={canonicalPath}
        imagePath={offer.imagePath}
        jsonLd={jsonLd}
      />

      <section className="lbf-container py-12 sm:py-16">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <NavLink to="/offres" className="hover:text-white">
              Offres
            </NavLink>
            <span>/</span>
            <span className="text-white/80">{offer.brand}</span>
          </div>

          <div className="grid gap-6 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7">
              <div className="lbf-card p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-center gap-4">
                    <img
                      src={offer.imagePath}
                      alt={offer.brand}
                      className="h-16 w-16 rounded-3xl border border-white/10 bg-white/5 object-contain p-2"
                      decoding="async"
                    />
                    <div>
                      <h1 className="text-3xl font-black text-white sm:text-4xl">{offer.brand}</h1>
                      <div className="mt-2 text-sm text-zinc-200/80">{offer.tagline}</div>
                      <div className="mt-3">
                        <Stars value={offer.rating} />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 sm:items-end">
                    <NavLink to="/comparatif-offres" className="lbf-btn-ghost">
                      Comparer <Icon icon={FiArrowRight} aria-hidden="true" />
                    </NavLink>
                    {offer.links.official ? (
                      <a className="lbf-btn-primary" href={offer.links.official} target="_blank" rel="noreferrer">
                        Voir l’offre <Icon icon={FiExternalLink} aria-hidden="true" />
                      </a>
                    ) : null}
                    <div className="text-xs text-zinc-400">Mise à jour: {offer.updatedAtISO}</div>
                  </div>
                </div>

                <p className="mt-5 text-sm text-zinc-200/85">{offer.heroPitch}</p>

                <div className="mt-6 flex flex-wrap gap-2">
                  <span className="lbf-badge">
                    <Icon icon={FiCheckCircle} aria-hidden="true" />
                    {offer.welcome.headline}
                  </span>
                  <span className="lbf-badge">
                    Retrait typique: ~{offer.payouts.typicalHours}h
                  </span>
                  <span className="lbf-badge">
                    Dépôt min: {offer.welcome.minDepositEur}€
                  </span>
                </div>

                <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-start gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/10 bg-white/5">
                      <Icon icon={FiInfo} aria-hidden="true" />
                    </div>
                    <div className="text-sm text-zinc-200/85">
                      Ce site propose une analyse. Les conditions officielles priment toujours. Joue uniquement si tu as
                      18+ et un budget clair.
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="lbf-card p-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-300/70">Idéal pour</div>
                  <ul className="mt-3 grid gap-2 text-sm text-zinc-200/85">
                    {offer.bestFor.map((x) => (
                      <li key={x}>- {x}</li>
                    ))}
                  </ul>
                </div>
                <div className="lbf-card p-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-300/70">Sports forts</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {offer.sports.map((s) => (
                      <span key={s} className="lbf-badge">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                <ScoreRing label="Expérience" score={offer.charts.uxScore} hint="Clarté, navigation, vitesse." />
                <ScoreRing label="Confiance" score={offer.charts.trustScore} hint="Lisibilité, retraits, stabilité." />
                <ScoreRing label="Promos" score={offer.charts.promoScore} hint="Qualité réelle des offres." />
              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-4 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="lbf-card p-6">
                <h2 className="text-xl font-black text-white">Bonus de bienvenue</h2>
                <p className="mt-3 text-sm text-zinc-200/85">{offer.welcome.details}</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-xs text-zinc-300/80">Maximum</div>
                    <div className="mt-2 text-lg font-black text-white">{offer.welcome.maxEur}€</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-xs text-zinc-300/80">Dépôt minimum</div>
                    <div className="mt-2 text-lg font-black text-white">{offer.welcome.minDepositEur}€</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-xs text-zinc-300/80">Règle d’or</div>
                    <div className="mt-2 text-sm font-bold text-white">Lire le turnover</div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="text-sm font-extrabold text-white">Activation en 3 étapes</div>
                  <ol className="mt-3 grid gap-2 text-sm text-zinc-200/85">
                    {offer.welcome.steps.map((s, idx) => (
                      <li key={s}>
                        <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-white/10 bg-white/5 text-xs font-bold text-white">
                          {idx + 1}
                        </span>
                        {s}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="lbf-card p-6">
                <h2 className="text-xl font-black text-white">Ce qu’on aime / à surveiller</h2>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-sm font-extrabold text-white">Points forts</div>
                    <ul className="mt-3 grid gap-2 text-sm text-zinc-200/85">
                      {offer.pros.map((x) => (
                        <li key={x}>- {x}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-sm font-extrabold text-white">À surveiller</div>
                    <ul className="mt-3 grid gap-2 text-sm text-zinc-200/85">
                      {offer.cons.map((x) => (
                        <li key={x}>- {x}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-4 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <MiniBarChart
                title="Qualité des cotes par sport"
                hint="Score indicatif: comparaison rapide, pas une promesse."
                items={offer.charts.oddsBySport}
              />
            </div>
            <div className="lg:col-span-6">
              <TimelineChart
                title="Parcours de retrait (exemple)"
                hint="Illustration typique quand le dossier est validé."
                points={offer.charts.payoutTimeline}
              />
            </div>
          </div>

          <div className="mt-10 lbf-card p-6">
            <h2 className="text-xl font-black text-white">Conseil “propre” avant de déposer</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {[
                {
                  t: 'Décide ton plafond',
                  d: 'Le bon pari commence avant le match: un budget, un nombre de mises, et tu coupes.',
                },
                {
                  t: 'Choisis une méthode de retrait',
                  d: 'Si tu sais déjà comment tu retires, tu évites 80% des hésitations.',
                },
                {
                  t: 'Lis le turnover',
                  d: 'Un bonus “gros” peut être moins intéressant qu’un bonus “simple”.',
                },
                {
                  t: 'Reste lucide',
                  d: 'Le sport est imprévisible. Ton avantage, c’est la discipline, pas la chance.',
                },
              ].map((x) => (
                <div key={x.t} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-sm font-extrabold text-white">{x.t}</div>
                  <div className="mt-2 text-sm text-zinc-200/85">{x.d}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-zinc-400">
                Besoin d’un choix rapide? Compare les 3 offres en 1 minute.
              </div>
              <NavLink to="/comparatif-offres" className="lbf-btn-primary">
                Ouvrir le comparatif <Icon icon={FiArrowRight} aria-hidden="true" />
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}


